import { z } from "zod";
import {
  catalogSchema,
  hydrateCatalog,
  orderSchema,
  type CancelReason,
  type CatalogWire,
  type DeliveredLine,
  type DraftOrder,
  type LatLng,
  type Order,
  type Product,
} from "@moomoo/core";

/**
 * The API client.
 *
 * Every response is parsed, not assumed: a deploy that changes a field should
 * surface as one clear error here rather than as `undefined` halfway down a
 * render. Every call has a timeout, because a phone on weak mobile data will
 * otherwise hang with a spinner forever.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly fields?: ReadonlyArray<{ path: string; message: string }>,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when trying again might work: offline, timeout, or our side failing. */
  get retryable(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

const errorBody = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fields: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    detail: z.unknown().optional(),
  }),
});

const slotResponse = z.object({
  deliveryDate: z.string(),
  sameDay: z.boolean(),
  msToCutoff: z.number(),
  countdown: z.string(),
});
export type Slot = z.infer<typeof slotResponse>;

const quoteResponse = z.object({
  km: z.number().nonnegative(),
  source: z.enum(["routed", "estimated"]),
  feeCents: z.number().int().nonnegative(),
});
export type Quote = z.infer<typeof quoteResponse>;

const orderResponse = z.object({ order: orderSchema, deduplicated: z.boolean().optional() });

/** Office orders carry the phone and address the public view strips. */
const officeOrderSchema = orderSchema.extend({ phone: z.string().optional() });
export type OfficeOrder = z.infer<typeof officeOrderSchema>;
const officeListResponse = z.object({ orders: z.array(officeOrderSchema) });
const officeOrderResponse = z.object({ order: officeOrderSchema });

export type AdvanceInput =
  | { stage: "confirmed" }
  | { stage: "on_the_way" }
  | { stage: "delivered"; delivered: DeliveredLine[]; payment: "cash" | "khqr" | "unpaid" }
  | { stage: "cancelled"; reason: string };

const configResponse = z.object({
  wholesaleOpen: z.boolean(),
  retailPayment: z.enum(["cash", "khqr"]),
  officePhone: z.string().nullable(),
});
export type AppConfig = z.infer<typeof configResponse>;

const DEFAULT_TIMEOUT = 12_000;

async function request<T>(
  path: string,
  // input is `unknown` because it comes off the wire; only the output is ours
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), init.timeoutMs ?? DEFAULT_TIMEOUT);

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      signal: controller.signal,
      headers: { accept: "application/json", ...(init.headers ?? {}) },
    });
  } catch (error) {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    throw new ApiError(
      offline
        ? "You appear to be offline. Check your connection and try again."
        : "We could not reach Moo Moo Farms. Please try again.",
      offline ? "offline" : "network",
      0,
      undefined,
      error,
    );
  } finally {
    clearTimeout(timer);
  }

  const text = await response.text();
  let json: unknown = {};
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // a proxy, captive portal or gateway page: HTML, not our API
      throw new ApiError(
        response.ok
          ? "We got an unexpected reply from the server."
          : "Something went wrong.",
        response.ok ? "bad_response" : "unknown",
        response.ok ? 502 : response.status,
      );
    }
  }

  if (!response.ok) {
    const parsed = errorBody.safeParse(json);
    if (parsed.success) {
      const { code, message, fields, detail } = parsed.data.error;
      throw new ApiError(message, code, response.status, fields, detail);
    }
    throw new ApiError("Something went wrong.", "unknown", response.status);
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError("We got an unexpected reply from the server.", "bad_response", 502);
  }
  return parsed.data;
}

export const api = {
  async catalog(): Promise<{ products: readonly Product[]; stale: boolean; fetchedAt: string }> {
    const wire: CatalogWire = await request("/api/catalog", catalogSchema);
    return { products: hydrateCatalog(wire), stale: wire.stale, fetchedAt: wire.fetchedAt };
  },

  slot(): Promise<Slot> {
    return request("/api/slot", slotResponse);
  },

  config(): Promise<AppConfig> {
    return request("/api/config", configResponse);
  },

  quote(pin: LatLng): Promise<Quote> {
    return request("/api/quote", quoteResponse, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(pin),
      timeoutMs: 8_000,
    });
  },

  async place(pin: LatLng): Promise<string | null> {
    const body = await request("/api/place", z.object({ label: z.string().nullable() }), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(pin),
      timeoutMs: 6_000,
    });
    return body.label;
  },

  async placeOrder(draft: DraftOrder): Promise<{ order: Order; deduplicated: boolean }> {
    const body = await request("/api/orders", orderResponse, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(draft),
      // generous: this one is worth waiting for rather than retrying blindly
      timeoutMs: 20_000,
    });
    return { order: body.order, deduplicated: body.deduplicated ?? false };
  },

  async order(id: string): Promise<Order> {
    return (await request(`/api/orders/${encodeURIComponent(id)}`, orderResponse)).order;
  },

  /** The customer pulls their own order. Refused once the driver has it. */
  async cancel(id: string, reason: CancelReason): Promise<Order> {
    const body = await request(`/api/orders/${encodeURIComponent(id)}/cancel`, orderResponse, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reason }),
      timeoutMs: 20_000,
    });
    return body.order;
  },

  office: {
    async login(pin: string): Promise<void> {
      await request("/api/office/login", z.unknown(), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pin }),
      });
    },
    async orders(scope: "today" | "open" | "all"): Promise<readonly OfficeOrder[]> {
      return (await request(`/api/office/orders?scope=${scope}`, officeListResponse)).orders;
    },
    async advance(id: string, input: AdvanceInput): Promise<OfficeOrder> {
      const body = await request(`/api/office/orders/${encodeURIComponent(id)}/stage`, officeOrderResponse, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
        timeoutMs: 20_000,
      });
      return body.order;
    },
  },
};
